/**
 * หุ่นไม้ (data/dummies.ts): the training yard's dummies. Each is a monster the World does not think for (World.step
 * skips it: `holds`); this system keeps it where it stands, calm and unhurt:
 *  - it never moves, casts or strikes, and a blow that "angers" it is forgotten the same step;
 *  - it never falls: its health is far above any blow, and a death by other means (a GM one-hit) is undone;
 *  - its health refills after `DUMMY.refillAfter` s without a blow;
 *  - the guarding dummy keeps its guard up (bar the stun of a break), with a stamina that never runs out, so only a
 *    break move (ท่าทำลาย) opens it;
 *  - it gives nothing (reward 0, no loot, never killed), and no draw is made from the random stream after setup.
 * Skills cost and cool down as usual: the player hits it by the ordinary rules.
 */
import { coreOf } from '../region/core';
import { DUMMY, GUARDING_DUMMIES, dummyYardIn, isDummyMonster } from '../data/dummies';
import type { Entity, WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

const STAMINA = 1_000_000;

interface Held {
  readonly e: Entity;
  readonly x: number;
  readonly y: number;
  readonly guard: boolean;
  /** Seconds since the last blow. */
  quiet: number;
}

export class DummiesSystem implements WorldSystem {
  readonly name = 'dummies';
  private readonly held = new Map<number, Held>();

  constructor(private readonly h: SystemHost) {}

  setup(): void {
    const yard = dummyYardIn(this.h.world.map.zone);
    if (!yard) return;
    // The yard is in the town's own metres; in a region the town sits at the core's origin.
    const o = coreOf(this.h.world.map);
    for (const d0 of yard.dummies) {
      const d = { ...d0, x: d0.x + o.x, y: d0.y + o.y };
      const e = this.h.spawnMonster(d.monster, d.x, d.y);
      Object.assign(e, { dodge: 0, stamina: STAMINA, maxStamina: STAMINA, toughness: 0 });
      this.held.set(e.id, { e, x: d.x, y: d.y, guard: GUARDING_DUMMIES.has(d.monster), quiet: DUMMY.refillAfter });
    }
  }

  /** Whether the World leaves this entity to the system. */
  holds(e: Entity): boolean {
    return this.held.has(e.id);
  }

  /** The dummies (for the HUD and the tests). */
  dummies(): Entity[] {
    return [...this.held.values()].map((d) => d.e);
  }

  observe(ev: WorldEvent): void {
    if (ev.type !== 'hit') return;
    const d = this.held.get(ev.tgtId);
    if (d) d.quiet = 0;
  }

  step(dt: number): void {
    for (const d of this.held.values()) {
      const e = d.e;
      d.quiet += dt;
      if (e.dead) Object.assign(e, { dead: false, deadT: 0, hp: e.maxHp });
      // The World's own per-step timers, which it does not run for a held dummy.
      e.stunT = Math.max(0, e.stunT - dt);
      e.hitT = Math.max(0, e.hitT - dt);
      Object.assign(e, { x: d.x, y: d.y, moving: false, cast: null, targetId: null, path: null, wander: null });
      if (e.brain) Object.assign(e.brain, { state: 'idle', foeId: null, blockT: 0 });
      e.stamina = e.maxStamina;
      e.blocking = d.guard && e.stunT <= 0;
      if (d.quiet >= DUMMY.refillAfter && e.hp < e.maxHp) e.hp = e.maxHp;
    }
  }
}

export { isDummyMonster };
